import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, CheckCircle2, MessageSquare, HelpCircle, Building2 } from "lucide-react";
import { toast } from "sonner";
import "@/mockups/deepcoast.css";
import { Section } from "@/mockups/dc";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", company: "", subject: "", message: "" };
const CONTACTS = [
  [Mail, "Email us", "Our team is here to help", <a href="mailto:kunal.patel@k9resorts.com" data-testid="contact-email-link">kunal.patel@k9resorts.com</a>],
  [Phone, "Call us", "Mon–Fri, 9am–6pm ET", <a href="tel:+12679152070" className="mono" data-testid="contact-phone-link">267-915-2070</a>],
  [MapPin, "Visit us", "Schedule an in-person meeting", <span>United States</span>],
];
const WHY = ["Custom software solutions tailored to your business", "Modern technology stack for reliability", "Responsive support team",
  "Proven track record with pet care facilities", "Seamless integration with existing systems"];
const FAQ = [["How long does setup take?", "Most customers are up and running in less than 30 minutes. We provide guided setup and training."],
  ["Do you offer a free trial?", "Yes! We offer a 14-day free trial with full access to all features. No credit card required."],
  ["Can I cancel anytime?", "Absolutely. No long-term contracts. Cancel anytime with 30 days notice."]];

const Field = ({ label, children }) => <label className="label" style={{ display: "flex", flexDirection: "column", gap: 4 }}>{label}{children}</label>;

const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, f);
      toast.success("Message sent. We'll get back to you within 24 hours.");
      setSent(true);
      setTimeout(() => { setF(EMPTY); setSent(false); }, 3000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send message. Please try again."); }
    setBusy(false);
  };
  if (sent) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0" }} data-testid="contact-success">
        <CheckCircle2 size={48} style={{ color: "var(--pos)", margin: "0 auto 12px" }} />
        <h3 style={{ fontSize: 20, fontWeight: 700 }}>Message sent</h3>
        <p className="muted" style={{ fontSize: 14 }}>Thank you for contacting us. We'll get back to you within 24 hours.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="contact-form">
      <Field label="Name *"><input className="input" required value={f.name} onChange={set("name")} placeholder="John Smith" data-testid="contact-name" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <Field label="Email *"><input type="email" className="input" required value={f.email} onChange={set("email")} placeholder="john@example.com" data-testid="contact-email" /></Field>
        <Field label="Phone"><input type="tel" className="input" value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="contact-phone" /></Field>
      </div>
      <Field label="Company"><input className="input" value={f.company} onChange={set("company")} placeholder="K9 Resorts" data-testid="contact-company" /></Field>
      <Field label="Subject *"><input className="input" required value={f.subject} onChange={set("subject")} placeholder="I'm interested in..." data-testid="contact-subject" /></Field>
      <Field label="Message *"><textarea className="input" required rows={5} value={f.message} onChange={set("message")} placeholder="Tell us more about your needs..." style={{ resize: "vertical" }} data-testid="contact-message" /></Field>
      <button type="submit" className="btn btn-primary" disabled={busy} style={{ justifyContent: "center", padding: "12px 16px" }} data-testid="contact-submit"><Send size={15} />{busy ? "Sending…" : "Send message"}</button>
    </form>
  );
};

const ContactUs = () => (
  <div className="dc v3" style={{ background: "transparent", display: "flex", flexDirection: "column", gap: 18 }} data-testid="contact-page">
    <div>
      <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Get in touch</h1>
      <p className="muted" style={{ fontSize: 15, margin: 0 }}>Have questions? Send us a message and we'll respond as soon as possible.</p>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
      {CONTACTS.map(([I, t, s, v]) => (
        <div key={t} className="card" style={{ padding: 18, display: "flex", gap: 14, alignItems: "flex-start" }} data-testid={`contact-card-${t.split(" ")[0].toLowerCase()}`}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-soft)", color: "var(--accent-ink)", display: "grid", placeItems: "center", flexShrink: 0 }}><I size={18} /></div>
          <div><div style={{ fontWeight: 700, fontSize: 15 }}>{t}</div><div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>{s}</div><div style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-ink)" }}>{v}</div></div>
        </div>
      ))}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16, alignItems: "start" }}>
      <Section title="Send us a message" icon={MessageSquare} testid="contact-form-section"><ContactForm /></Section>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Section title="Why choose Ariya Ventures Inc?" icon={Building2} testid="contact-why">
          <ul style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
            {WHY.map((w) => <li key={w} style={{ display: "flex", gap: 8 }}><CheckCircle2 size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />{w}</li>)}
          </ul>
        </Section>
        <Section title="Frequently asked questions" icon={HelpCircle} testid="contact-faq">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {FAQ.map(([q, a]) => <div key={q}><div style={{ fontWeight: 700, fontSize: 14 }}>{q}</div><div className="muted" style={{ fontSize: 13 }}>{a}</div></div>)}
          </div>
        </Section>
        <div className="card" style={{ padding: 20, background: "var(--accent-ink)", color: "#fff", borderColor: "var(--accent-ink)" }} data-testid="contact-demo">
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Ready to get started?</div>
          <p style={{ fontSize: 14, opacity: 0.9, margin: "0 0 12px" }}>Schedule a personalized demo to see how we can help your business grow.</p>
          <a href="mailto:kunal.patel@k9resorts.com?subject=FourPawIQ%20demo" className="btn" style={{ background: "#fff", color: "var(--accent-ink)", borderColor: "#fff" }} data-testid="contact-demo-btn">Schedule demo</a>
        </div>
      </div>
    </div>
    <div className="card" style={{ padding: 18, textAlign: "center" }} data-testid="contact-company-footer">
      <div style={{ fontWeight: 800, fontSize: 18 }}>Ariya Ventures Inc</div>
      <div className="muted" style={{ fontSize: 13 }}>Building custom software solutions for modern businesses · Incorporated · Secure & reliable · Enterprise ready</div>
    </div>
  </div>
);

export default ContactUs;
